<template>
  <div class="waajo-ticket-gallery">
    <div
      v-for="(item, index) in items"
      :key="item.key"
      class="waajo-ticket-gallery-item"
      :class="{ 'is-busy': busyId === item.id }"
    >
      <button
        type="button"
        class="waajo-ticket-gallery-thumb"
        :title="'Pratinjau ' + item.name"
        @click="$emit('preview', index)"
      >
        <img
          v-if="item.url && !item.broken"
          :src="item.url"
          :alt="item.name"
          class="waajo-ticket-gallery-image"
          @error="$emit('image-error', item)"
        />
        <span v-else class="waajo-ticket-gallery-broken">
          <i class="fa fa-picture-o"></i>
          <small>{{ item.broken ? 'Pratinjau kedaluwarsa' : 'Tanpa pratinjau' }}</small>
        </span>
        <span v-if="busyId === item.id" class="waajo-ticket-gallery-overlay">
          <i class="fa fa-spinner fa-spin"></i>
        </span>
      </button>

      <div class="waajo-ticket-gallery-meta">
        <strong :title="item.name">{{ item.name }}</strong>
        <small>
          {{ formatSize(item.size) }}
          <template v-if="item.width && item.height"> · {{ item.width }}×{{ item.height }}</template>
        </small>
      </div>

      <div class="waajo-ticket-gallery-actions">
        <button
          type="button"
          class="waajo-ticket-gallery-action"
          title="Pratinjau gambar"
          :disabled="busyId === item.id"
          @click="$emit('preview', index)"
        >
          <i class="fa fa-search-plus"></i>
        </button>
        <button
          v-if="downloadable"
          type="button"
          class="waajo-ticket-gallery-action"
          title="Unduh gambar"
          :disabled="busyId === item.id || downloadingId === item.id"
          @click="$emit('download', item)"
        >
          <i :class="downloadingId === item.id ? 'fa fa-spinner fa-spin' : 'fa fa-download'"></i>
        </button>
        <button
          v-if="removable"
          type="button"
          class="waajo-ticket-gallery-action is-danger"
          title="Hapus gambar"
          :disabled="busyId === item.id || disabled"
          @click="$emit('remove', item)"
        >
          <i class="fa fa-trash"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTicketFileSize } from '@/utils/ticketAttachment'

export default {
  name: 'TicketAttachmentGrid',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    removable: {
      type: Boolean,
      default: false
    },
    downloadable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    busyId: {
      type: [String, Number],
      default: null
    },
    downloadingId: {
      type: [String, Number],
      default: null
    }
  },
  methods: {
    formatSize(size) {
      return formatTicketFileSize(size)
    }
  }
}
</script>

<style scoped>
.waajo-ticket-gallery {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.waajo-ticket-gallery-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 10px;
  border: 1px solid var(--waajo-border);
  border-radius: 12px;
  background: #fff;
}
.waajo-ticket-gallery-item.is-busy {
  opacity: 0.7;
}
.waajo-ticket-gallery-thumb {
  position: relative;
  display: block;
  width: 100%;
  height: 0;
  padding: 0 0 72%;
  overflow: hidden;
  border: 1px solid #eef2f7;
  border-radius: 9px;
  background: var(--waajo-surface-soft);
  cursor: zoom-in;
}
.waajo-ticket-gallery-image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.waajo-ticket-gallery-broken {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px;
  color: var(--waajo-text-faint);
  font-size: 18px;
}
.waajo-ticket-gallery-broken small {
  font-size: 11px;
  line-height: 1.35;
}
.waajo-ticket-gallery-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  color: #fff;
  font-size: 18px;
}
.waajo-ticket-gallery-meta {
  min-width: 0;
}
.waajo-ticket-gallery-meta strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 700;
  color: var(--waajo-text);
}
.waajo-ticket-gallery-meta small {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-gallery-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
}
.waajo-ticket-gallery-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: var(--waajo-text-body);
  font-size: 12px;
  line-height: 1;
}
.waajo-ticket-gallery-action:hover,
.waajo-ticket-gallery-action:focus {
  border-color: var(--waajo-brand-border-strong);
  color: var(--waajo-brand-ink);
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-gallery-action.is-danger:hover,
.waajo-ticket-gallery-action.is-danger:focus {
  border-color: #fecaca;
  color: #b91c1c;
  background: #fef2f2;
}
.waajo-ticket-gallery-action:disabled {
  opacity: 0.55;
}
@media (max-width: 992px) {
  .waajo-ticket-gallery {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 560px) {
  .waajo-ticket-gallery {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .waajo-ticket-gallery-item {
    padding: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketAttachmentGrid.vue